/**
 * Onboarding next-action step (APP-002 §10).
 *
 * The final step offers the two primary actions (create an API key, try the
 * playground), plus a clear "Go to dashboard". The recommended first action is
 * highlighted. No API key is auto-created here.
 *
 * The two action destinations are real links (semantic anchors with hrefs, so
 * screen readers announce them as links and the URLs are inspectable), styled
 * as buttons. Completion is handled by the parent route: each link's onClick
 * marks onboarding complete BEFORE react-router performs the client-side
 * navigation, so the account gate lets the user land on the chosen route
 * instead of bouncing them back into a remounted onboarding.
 */
import { Link } from 'react-router-dom';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Key, Sparkles } from '@/components/icons';

export interface NextActionStepProps {
  /** Called when the user explicitly finishes (mark complete + go to dashboard). */
  onFinish: () => void;
  /**
   * Mark onboarding complete. Fired by the action links' onClick immediately
   * before the link navigates; completing first is what lets the gate admit
   * the navigation.
   */
  onComplete: () => void;
}

export function NextActionStep({ onFinish, onComplete }: NextActionStepProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight">You are ready to build</h2>
        <p className="text-sm text-muted-foreground">
          Create an API key to start integrating, or explore the playground. The playground runs under your
          dashboard session, so you can try it without a key.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {/* Recommended first action highlighted (APP-002 §10). */}
        <Card className="border-primary/40 ring-1 ring-primary/20">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4 text-primary" />
              <CardTitle className="text-base">Create an API key</CardTitle>
            </div>
            <CardDescription>Recommended first step. Generate a key to call the API.</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/api-keys" replace className={buttonVariants({ size: 'sm' })} onClick={onComplete}>
              Go to API Keys
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <CardTitle className="text-base">Try the playground</CardTitle>
            </div>
            <CardDescription>Test the API under your real plan and quota.</CardDescription>
          </CardHeader>
          <CardContent>
            <Link
              to="/playground"
              replace
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
              onClick={onComplete}
            >
              Open playground
            </Link>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardContent className="flex flex-col items-start gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-0.5">
            <p className="text-sm font-medium">Ready to explore?</p>
            <p className="text-xs text-muted-foreground">
              Head to your dashboard for plan, usage, and account overview.
            </p>
          </div>
          <Button size="sm" variant="secondary" onClick={onFinish}>
            Go to dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
